import { request } from './request'
import { ERROR_TYPES, METHODS, STATUS_CODES } from '../config/constants'
import { AxiosError } from 'axios'
import type { CoreError, CoreResult } from '../interfaces/common'
import { fail } from 'src/utils/notify'
import { NotFoundError } from './errors'

export interface ILoader {
  service: string
}

export interface ILoaderConfig {
  uri: string
  subUri?: string
}

export interface IOptionalParams {
  contentType?: string
  headers?: Record<string, string>
  params?: Record<string, any>
  responseType?: string
}

export interface IStoreOption {
  useGlobalLoadingIndicator?: boolean
}

export class Loader {
  service: string

  constructor({ service }: ILoader) {
    this.service = service
  }

  getAll = async <T>(
    config: ILoaderConfig,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T[]>> => {
    try {
      const { data } = await request<T[]>({
        url: `${this.service}/${config.uri}`,
        method: METHODS.get,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  getByPage = async <T>(
    config: ILoaderConfig,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T[]>> => {
    try {
      const { data } = await request<T[]>({
        url: `${this.service}/${config.uri}`,
        method: METHODS.get,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  getById = async <T>(
    config: ILoaderConfig,
    id: string,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data } = await request<T>({
        url: `${this.service}/${config.uri}/${id}${this.getSubUri(config)}`,
        method: METHODS.get,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      // handle error
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  post = async <T>(
    config: ILoaderConfig,
    payload: T,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data, status } = await request<T>({
        url: `${this.service}/${config.uri}`,
        method: METHODS.post,
        data: payload,
        ...opt,
      })
      return { data, errorData: null, status }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  putById = async <T>(
    config: ILoaderConfig,
    id: string,
    payload: T,
    custom = false,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data, status } = await request<T>({
        url: custom
          ? `${this.service}/${config.uri}`
          : `${this.service}/${config.uri}/${id}${this.getSubUri(config)}`,
        method: METHODS.put,
        data: payload,
        ...opt,
      })
      return { data, errorData: null, status }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  patch = async <T>(
    config: ILoaderConfig,
    id: string,
    payload: T,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data } = await request<T>({
        url: `${this.service}/${config.uri}`,
        method: METHODS.patch,
        data: payload,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  delete = async <T>(
    config: ILoaderConfig,
    id: string,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data } = await request<T>({
        url: `${this.service}/${config.uri}/${id}`,
        method: METHODS.delete,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  deleteBulk = async <T>(
    config: ILoaderConfig,
    payload: T[],
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data } = await request<T>({
        url: `${this.service}/${config.uri}`,
        method: METHODS.delete,
        data: payload,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  putIndexing = async <T>(
    config: ILoaderConfig,
    payload: T,
    opt?: IOptionalParams,
  ): Promise<CoreResult<T>> => {
    try {
      const { data } = await request<T>({
        url: `${this.service}/index/${config.uri}`,
        method: METHODS.put,
        data: payload,
        ...opt,
      })
      return { data, errorData: null }
    } catch (err) {
      return {
        errorData: this.errorHandler(err as AxiosError),
        data: null,
      }
    }
  }

  protected errorHandler = (err: AxiosError): CoreError | null => {
    let message = 'messagesGroup.unknownError'
    let errors: any[] = []
    if (err.response?.status === STATUS_CODES.internalError) {
      message = 'messagesGroup.internalError'
    }
    if (err.response?.status === STATUS_CODES.notFound) {
      if (err.response.data) message = (err.response.data as any).message
      throw new NotFoundError(message, errors)
    }
    if (err.response?.status === STATUS_CODES.badRequest) {
      if (err.response.data) {
        message = (err.response.data as any).message
        errors = (err.response.data as any).errors
      }
      return {
        name: ERROR_TYPES.validationError,
        message,
        errors,
      }
    }
    if (!err.response && err.code && err.code === AxiosError.ERR_NETWORK) {
      message = 'messagesGroup.networkError'
      return {
        name: ERROR_TYPES.networkError,
        message,
        errors,
      }
    }
    if (err.response?.status === STATUS_CODES.forbidden) {
      message = 'messagesGroup.forbidden'
      fail(message)
      return {
        name: ERROR_TYPES.validationError,
        message,
        errors,
      }
    }
    if (err.response?.status === STATUS_CODES.unauthorized) {
      message = ''
      return null
    }

    fail(message)
    return {
      name: ERROR_TYPES.unknownError,
      message,
      errors,
    }
  }

  private getSubUri = (config: ILoaderConfig) =>
    config.subUri ? `/${config.subUri}` : ''
}
